import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '../../../../lib';
import { ComponentsModule, DrawerComponent } from '../../../../lib';
import { RoomListItem, RoomType } from '../../models';
import { SetRoomsEvent, Level } from '../../models';
import { LevelNamePipe, TranslateRoomPipe } from '../../pipes';
import { MatDividerModule } from '@angular/material/divider';

@Component({
    selector: 'ca-rbr-add-rooms',
    templateUrl: './add-rooms.component.html',
    styleUrl: './add-rooms.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MaterialModule, ComponentsModule, TranslatePipe, LevelNamePipe, TranslateRoomPipe, MatDividerModule]
})
export class AddRoomsComponent implements AfterViewInit {
    private translate = inject(TranslateService);
    private readonly allRoomTypes = Object.values(RoomType);

    level = input<Level | null>();
    levels = input<Level[]>([]);
    openOnInit = input(false);
    editedLevel = signal<Level | null | undefined>(null);
    hasUpperLevels = computed(() => this.levels().some(level => level.number > 0));
    hasLowerLevels = computed(() => this.levels().some(level => level.number < 0));
    confirmButtonDisabled = computed(() => this.areRoomCountsEqual(this.initialRoomCounts(), this.roomCounts()));
    roomsData = input<RoomListItem[] | null | undefined>(undefined);
    setRooms = output<SetRoomsEvent>();
    roomCounts = signal<Partial<Record<RoomType, number>>>({});
    initialRoomCounts = signal<Partial<Record<RoomType, number>>>({});
    roomTypes = [...this.allRoomTypes];
    searchTerm = '';

    @ViewChild('drawer', { static: true }) drawer: DrawerComponent;

    ngAfterViewInit(): void {
        if (this.openOnInit()) {
            this.openDrawer();
        }
    }

    openDrawer(): void {
        this.editedLevel.set(this.level());
        this.initRoomCountsFromLevel();
        this.drawer.open();
    }

    search(): void {
        const searchTerm = this.searchTerm.trim().toLowerCase();

        if (!searchTerm) {
            this.roomTypes = [...this.allRoomTypes];
            return;
        }

        this.roomTypes = this.allRoomTypes.filter(roomType =>
            this.translate.instant(`RBR.ROOM_TYPE.${roomType}`).toLowerCase().includes(searchTerm)
        );
    }

    changeRoomCount(roomType: RoomType, count: number | undefined): void {
        if (count === undefined) {
            return;
        }
        if (count > 0) {
            this.roomCounts.update(current => ({
                ...current,
                [roomType]: count
            }));
            return;
        }

        this.roomCounts.update(current => {
            const { [roomType]: _toDelete, ...rest } = current;
            return rest;
        });
    }

    confirm(): void {
        const level = this.editedLevel();

        if (!level) {
            return;
        }

        this.setRooms.emit({
            rooms: this.roomCounts(),
            levelId: level.id
        });

        this.roomCounts.set({});
        this.initialRoomCounts.set({});
        this.drawer.close();
    }

    changeEditedLevel(levelId: string): void {
        const editedLevel = this.levels().find(level => level.id === levelId);
        if (!editedLevel) {
            return;
        }

        this.editedLevel.set(editedLevel);
        this.initRoomCountsFromLevel();
    }

    private initRoomCountsFromLevel(): void {
        const level = this.editedLevel();

        if (!level) {
            this.roomCounts.set({});
            this.initialRoomCounts.set({});
            return;
        }

        const counts = level.roomsData.reduce<Partial<Record<RoomType, number>>>((acc, room) => {
            acc[room.type] = (acc[room.type] || 0) + 1;
            return acc;
        }, {});

        this.roomCounts.set(counts);
        this.initialRoomCounts.set({ ...counts });
    }

    private areRoomCountsEqual(a: Partial<Record<RoomType, number>>, b: Partial<Record<RoomType, number>>): boolean {
        return Object.values(RoomType).every(roomType => (a[roomType] ?? 0) === (b[roomType] ?? 0));
    }
}
